/**
 * Login Page - Trade Loop v2.0
 *
 * Combined Sign In / Sign Up page with toggle
 * Migrated from v1.0 with cookie-based authentication
 */

import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { ArrowLeft, Eye, EyeOff, Mail, Loader2, ChevronLeft, UserPlus, Sparkles, Fingerprint } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, describeApiError } from "@/lib/api";
import { signInWithGoogle, signInWithApple } from "@/services/authService";
import { getGoogleIdToken, mapNativeGoogleError } from "@/services/nativeGoogleAuth";
import { getAppleIdToken, mapNativeAppleError } from "@/services/nativeAppleAuth";
import { getStoreFailure } from "@/services/nativeSession";
import { isNative, isIOS } from "@/utils/platform";
import { GOOGLE_SIGN_IN_AVAILABLE } from "@/lib/env";

/**
 * Where "Continue with Apple" is offered at all.
 *
 * - **Web (any OS, including Android browsers)** — yes, via the Supabase
 *   redirect flow.
 * - **Native iOS** — yes, via the OS sheet (AuthenticationServices).
 * - **Native Android** — NO. Apple sign-in on Android is a web redirect, and a
 *   bundled Capacitor build cannot use one: its webview origin is
 *   `https://localhost`, which is not (and cannot be) a registered Supabase
 *   redirect target. The capgo plugin's Android Apple path needs its own
 *   clientId/redirectUrl, which we deliberately do not configure because iOS
 *   derives everything from the bundle id. Rendering the button there would give
 *   Android users a control that always fails.
 *
 * Google has no equivalent restriction — it ships native client IDs for both
 * platforms — which is why this asymmetry exists only for Apple.
 */
const APPLE_SIGN_IN_AVAILABLE = !isNative || isIOS;
import { safeRedirectPath } from "@/utils/safeRedirect";
import { useEffect, useState, useRef } from "react";
import { useToast } from "@/contexts/ToastContext";
import { FREE_TRIAL_ENABLED } from "@/config/subscription-plan-limits";
import { EMAIL_OTP_LENGTH, createEmptyOtpDigits } from "@/config/auth-otp";
import { APPLE_HIDDEN_EMAIL_NOTICE, APPLE_HIDE_MY_EMAIL_SIGNUP_NOTE } from "@/config/auth-notices";
import PrivacyAcknowledgementModal from "@/components/auth/PrivacyAcknowledgementModal";
import tradeLoopLogo from "@/assets/trade-loop-logo.svg";
import { useBiometric } from "@/hooks/useBiometric";
import AppStoreBadges, { APP_STORE_BADGES_VISIBLE } from "@/components/AppStoreBadges";

type AuthMode = 'signin' | 'signup';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // A beta-gated OAuth attempt that the backend rejected bounces the user back
  // here with a code in navigation state (BETA_GATED_OAUTH_PLAN.md §5): open the
  // signup tab and surface a gentle message rather than a raw failure.
  const oauthGateError = (location.state as { oauthError?: string } | null)?.oauthError;
  // Which provider was rejected, so the notice below can name it. Carried from
  // AuthCallbackPage; absent for an older/unknown bounce, which falls back to
  // provider-neutral copy rather than guessing.
  const oauthGateProvider = (location.state as { oauthProvider?: string } | null)?.oauthProvider;
  // Detect a referral invite token in the URL on the very first render so it can
  // survive the unauthenticated → authenticated transition. We also fall back to
  // localStorage in case the URL was rewritten (OAuth round-trip, OTP email link).
  const referralInviteToken =
    searchParams.get('referralInviteToken') ||
    (typeof window !== 'undefined' ? localStorage.getItem('pendingReferralInviteToken') : null);
  // Sanitized: `redirect` is attacker-controllable on a public URL, and a
  // protocol-relative value like `//evil.com` would navigate off-site (utils/safeRedirect.ts).
  const rawRedirect = safeRedirectPath(searchParams.get('redirect'), '/dashboard');
  const redirectTo = referralInviteToken
    ? `/referrals?claimToken=${encodeURIComponent(referralInviteToken)}`
    : rawRedirect;
  const { isAuthenticated, isLoading: authLoading, login, signup, verifyOtp, resendVerification, signInWithGoogleNative, signInWithAppleNative } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();

  // Biometric authentication
  const {
    isAvailable: isBiometricAvailable,
    biometryTypeName,
    hasCredentials: hasBiometricCredentials,
    isAuthenticating: isBiometricAuthenticating,
    authenticate: biometricAuthenticate,
    saveCredentials: saveBiometricCredentials,
  } = useBiometric();

  // Auth mode toggle. A rejected OAuth signup/login lands on the signup tab —
  // except APPLE_HIDDEN_EMAIL, where the user already HAS an account and the
  // whole point is to get them onto the sign-in tab they normally use.
  // `?mode=signup` opens straight on the sign-up tab, so a link can point someone at account
  // creation and still give them Continue with Google / Apple and the toggle back to sign-in.
  // A rejected OAuth attempt still wins: that state is about recovering the user, and it must not
  // be overridden by whatever tab the incoming link asked for.
  const [mode, setMode] = useState<AuthMode>(
    oauthGateError === 'NO_ACCOUNT'
      ? 'signup'
      : searchParams.get('mode') === 'signup'
        ? 'signup'
        : 'signin'
  );

  // Graceful notice shown after a rejected OAuth attempt. The provider is named
  // only when we actually know it — this copy said "Google" unconditionally,
  // which became wrong the moment Apple could reach this path.
  const [oauthNotice, setOauthNotice] = useState<{ code: string; message: string } | null>(
    oauthGateError === 'APPLE_HIDDEN_EMAIL'
      ? { code: 'APPLE_HIDDEN_EMAIL', message: APPLE_HIDDEN_EMAIL_NOTICE.message }
      : oauthGateError === 'NO_ACCOUNT'
        ? {
            code: 'NO_ACCOUNT',
            message: oauthGateProvider === 'apple'
              ? 'No account found for that Apple login — sign up first.'
              : oauthGateProvider === 'google'
                ? 'No account found for that Google login — sign up first.'
                : 'No account found for that login — sign up first.',
          }
        : null
  );

  // Biometric save prompt state
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);

  // Common state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);

  // Social signup used to have its own phone input separate from the email
  // form's copy (BETA_GATED_OAUTH_PLAN.md §6). The signup UI now collects
  // firstName/lastName/phone once at the top of the tab and both paths share
  // those values — the OAuth handlers read them straight from the shared
  // state below.

  // Signup-specific state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signupComplete, setSignupComplete] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [referrerId, setReferrerId] = useState<string | null>(null);
  const [startTrial, setStartTrial] = useState<boolean>(false);
  // Team-member invite carried over from the retired /signup page. A non-null
  // token puts this page into org-invite mode: email is locked to the invited
  // address, the social buttons are hidden, and submitting goes to the
  // invite-specific backend route instead of ordinary signup.
  const [orgInviteToken, setOrgInviteToken] = useState<string | null>(null);

  // Gate for all "Sign Up" buttons — Google, Apple, and the email/password
  // Create Account form. The product rule is that no user can create an
  // account without a first and last name: the CRM + service-area modal that
  // fires on first login also expects a real display name, and Google/Apple
  // don't reliably supply one. Checking trim() rather than length so a user
  // cannot bypass the gate by typing spaces.
  const canSignup = firstName.trim().length > 0 && lastName.trim().length > 0;

  // OTP verification state
  const [otpCode, setOtpCode] = useState<string[]>(createEmptyOtpDigits());
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Redirect authenticated users to intended destination.
  // This handles only the "already logged in, visited /login directly" case.
  // Once we've observed an unauthenticated settled state in this component's
  // lifetime, the auto-redirect is disabled so a racy setUser(null) → navigate
  // sequence from SessionExpirationHandler can't bounce the user back to
  // /dashboard. Manual login handlers call navigate(redirectTo) directly.
  const hasSettledUnauthenticated = useRef(false);
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      hasSettledUnauthenticated.current = true;
    }
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !authLoading &&
      !showBiometricPrompt &&
      !hasSettledUnauthenticated.current
    ) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, authLoading, navigate, redirectTo, showBiometricPrompt]);

  // Check for referral and trial parameters on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get('ref');
    const trialParam = urlParams.get('trial');
    const referralInviteParam = urlParams.get('referralInviteToken');

    if (refParam) {
      setReferrerId(refParam);
      localStorage.setItem("pendingReferrerId", refParam);
    }

    if (trialParam === 'true' && FREE_TRIAL_ENABLED) {
      setStartTrial(true);
      setMode('signup'); // Auto-switch to signup if trial param
      localStorage.setItem("pendingTrial", "true");
    }

    // Persist the referral invite token so it survives any auth round-trip
    // (OAuth, email confirmation link, OTP). ReferralsPage and AuthCallbackPage
    // both consume this and clear it once the referral has been claimed.
    if (referralInviteParam) {
      localStorage.setItem("pendingReferralInviteToken", referralInviteParam);
    }

    // Organization (team member) invite, ported from the retired /signup page.
    // Deliberately NOT stashed in localStorage the way the referral token is:
    // this path is email/password only, so there is no round-trip to survive,
    // and a stale stash would leak an invite into an unrelated later signup.
    const orgInviteParam = urlParams.get('orgInviteToken');
    if (orgInviteParam) {
      setOrgInviteToken(orgInviteParam);
      setMode('signup');

      // The invite URL carries the invited person's details so they don't
      // retype them. Email is authoritative — the backend rejects a mismatch
      // with EMAIL_MISMATCH — hence the locked field in the form below.
      const emailParam = urlParams.get('email');
      const firstNameParam = urlParams.get('firstName');
      const lastNameParam = urlParams.get('lastName');
      if (emailParam) setEmail(emailParam);
      if (firstNameParam) setFirstName(firstNameParam);
      if (lastNameParam) setLastName(lastNameParam);
    }
  }, []);

  // Cooldown timer for resend code
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Render a failed native Google attempt (nativeGoogleAuth.mapNativeGoogleError
  // decides *what* the outcome is; this only applies it to the page). A user
  // dismissing the OS account picker is not an error and shows nothing.
  const applyNativeGoogleOutcome = (error: unknown) => {
    const outcome = mapNativeGoogleError(error);
    switch (outcome.kind) {
      case 'cancelled':
        return;
      case 'no_account':
        // Same landing as the web bounce (BETA_GATED_OAUTH_PLAN.md §5) — but on
        // native we're already on this page, so switch tabs in place instead of
        // navigating with router state.
        setMode('signup');
        setOauthNotice({ code: 'NO_ACCOUNT', message: outcome.message });
        return;
      default:
        showError(outcome.title, outcome.message);
    }
  };

  // Google sign-in handler.
  //
  // WEB: a full-page redirect to Supabase, so the loading state is only visible
  // for the brief moment before the browser navigates away. The referral-invite
  // token (if any) is already persisted to localStorage above, so it survives the
  // OAuth round-trip and is re-applied by AuthCallbackPage on return.
  //
  // NATIVE (bundled Capacitor): no browser and no redirect — the OS account
  // picker returns a Google id_token which is exchanged for the session cookie
  // in place, so this component stays mounted and navigates itself
  // (GOOGLE_OAUTH_NATIVE_MOBILE_PLAN.md §4/§10).
  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);

    if (!isNative) {
      try {
        signInWithGoogle('login', redirectTo);
      } catch (error: any) {
        setIsGoogleLoading(false);
        showError("Google sign-in failed", error.message || "Unable to start Google sign-in");
      }
      return;
    }

    try {
      const { idToken, nonce } = await getGoogleIdToken();
      await signInWithGoogleNative({ idToken, nonce, intent: 'login' });
      navigate(redirectTo);
    } catch (error) {
      applyNativeGoogleOutcome(error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // "Sign up with Google" handler. Signup is open to everyone (2026-08-31), so
  // this goes straight to the provider — the CRM question that used to be
  // asked here has moved to the post-login onboarding modal so every signup
  // path (email, Google, Apple) is answered in one place. The first/last name
  // the user typed on the shared block above is forwarded so the profile is
  // created with what the user actually saw, not with whatever Google returns.
  const handleGoogleSignup = async () => {
    setIsGoogleLoading(true);

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    // Web: full-page redirect to Google. Nothing after this runs.
    if (!isNative) {
      try {
        signInWithGoogle('signup', redirectTo, {
          phone: phone || undefined,
          firstName: trimmedFirst || undefined,
          lastName: trimmedLast || undefined,
        });
      } catch (error: any) {
        setIsGoogleLoading(false);
        showError("Google sign-in failed", error.message || "Unable to start Google sign-in");
      }
      return;
    }

    // Native: open the OS account picker and post the id_token to the gate.
    try {
      const { idToken, nonce } = await getGoogleIdToken();
      await signInWithGoogleNative({
        idToken,
        nonce,
        intent: 'signup',
        phone: phone || undefined,
        firstName: trimmedFirst || undefined,
        lastName: trimmedLast || undefined,
        // Forward the /connect/<uuid> invite referrer, same as the web OAuth
        // path (AuthCallbackPage.tsx). Cleared on both branches so a stale
        // value cannot leak into a later, unrelated signup.
        referralCode: referrerId || undefined,
      });
      if (referrerId) {
        localStorage.removeItem('pendingReferrerId');
      }
      navigate(redirectTo);
    } catch (error) {
      applyNativeGoogleOutcome(error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // Apple counterpart of applyNativeGoogleOutcome. The beta-gate rejections land
  // in the same places, so a user bounced from an Apple login sees the identical
  // signup-tab treatment they would from a Google one.
  const applyNativeAppleOutcome = (error: unknown) => {
    const outcome = mapNativeAppleError(error);
    switch (outcome.kind) {
      case 'cancelled':
        return;
      case 'no_account':
        setMode('signup');
        setOauthNotice({ code: 'NO_ACCOUNT', message: outcome.message });
        return;
      case 'apple_hidden_email':
        // Deliberately the sign-in tab, not signup: this user already has an
        // account and the instruction is to reach it the way they always do
        // (APPLE_HIDDEN_EMAIL_LINKING.md §3 #4). Sending them to signup is the
        // duplicate-account bug.
        setMode('signin');
        setOauthNotice({ code: 'APPLE_HIDDEN_EMAIL', message: outcome.message });
        return;
      default:
        showError(outcome.title, outcome.message);
    }
  };

  // Apple sign-in handler — same web/native split as handleGoogleLogin.
  //
  // WEB: full-page redirect to Supabase, which owns the whole Apple handshake
  // (client secret, form_post callback). We come back to /auth/callback with the
  // session in the hash, exactly as with Google.
  //
  // NATIVE (bundled Capacitor): the OS sheet returns an Apple id_token which is
  // exchanged for the session cookie in place. The first-authorization name is
  // forwarded here because this is the only moment it exists.
  const handleAppleLogin = async () => {
    setIsAppleLoading(true);

    if (!isNative) {
      try {
        signInWithApple('login', redirectTo);
      } catch (error: any) {
        setIsAppleLoading(false);
        showError("Apple sign-in failed", error.message || "Unable to start Apple sign-in");
      }
      return;
    }

    // Native Android never reaches here — the button is hidden
    // (APPLE_SIGN_IN_AVAILABLE). Guarded anyway so a future caller cannot
    // silently invoke the unconfigured Android plugin path.
    if (!isIOS) {
      setIsAppleLoading(false);
      return;
    }

    try {
      const { idToken, nonce, givenName, familyName } = await getAppleIdToken();
      await signInWithAppleNative({ idToken, nonce, givenName, familyName, intent: 'login' });
      navigate(redirectTo);
    } catch (error) {
      applyNativeAppleOutcome(error);
    } finally {
      setIsAppleLoading(false);
    }
  };

  // "Continue with Apple" on the signup tab. Shares the shared name + phone
  // block with the Google signup path; CRM lives on the post-login modal.
  const handleAppleSignup = async () => {
    setIsAppleLoading(true);

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    // Web: full-page redirect to Apple. Nothing after this runs.
    if (!isNative) {
      try {
        signInWithApple('signup', redirectTo, {
          phone: phone || undefined,
          firstName: trimmedFirst || undefined,
          lastName: trimmedLast || undefined,
        });
      } catch (error: any) {
        setIsAppleLoading(false);
        showError("Apple sign-in failed", error.message || "Unable to start Apple sign-in");
      }
      return;
    }

    // See handleAppleLogin — Android native has no working Apple path.
    if (!isIOS) {
      setIsAppleLoading(false);
      return;
    }

    try {
      const { idToken, nonce, givenName, familyName } = await getAppleIdToken();
      await signInWithAppleNative({
        idToken,
        nonce,
        givenName,
        familyName,
        intent: 'signup',
        phone: phone || undefined,
        firstName: trimmedFirst || undefined,
        lastName: trimmedLast || undefined,
        // See the Google-native branch above.
        referralCode: referrerId || undefined,
      });
      if (referrerId) {
        localStorage.removeItem('pendingReferrerId');
      }
      navigate(redirectTo);
    } catch (error) {
      applyNativeAppleOutcome(error);
    } finally {
      setIsAppleLoading(false);
    }
  };

  // Biometric login handler
  const handleBiometricLogin = async () => {
    const result = await biometricAuthenticate();

    if (result.success && result.email && result.password) {
      setIsLoading(true);
      try {
        await login(result.email, result.password);
        showSuccess("Welcome back!", `Signed in with ${biometryTypeName}`);
        navigate(redirectTo);
      } catch (error: any) {
        showError("Login failed", error.message || "Please try signing in manually");
      } finally {
        setIsLoading(false);
      }
    } else if (result.error) {
      // Only show error if it's not a user cancellation
      if (!result.error.toLowerCase().includes('cancel')) {
        showError("Authentication failed", result.error);
      }
    }
  };

  // Handle saving biometric credentials
  const handleSaveBiometricCredentials = async () => {
    if (pendingCredentials) {
      const success = await saveBiometricCredentials(pendingCredentials.email, pendingCredentials.password);
      if (success) {
        showSuccess(`${biometryTypeName} enabled`, "You can now sign in with biometrics");
      }
    }
    setShowBiometricPrompt(false);
    setPendingCredentials(null);
    navigate(redirectTo);
  };

  // Skip saving biometric credentials
  const handleSkipBiometric = () => {
    setShowBiometricPrompt(false);
    setPendingCredentials(null);
    navigate(redirectTo);
  };

  // Sign in submit handler
  const handleSignInSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Read values from form elements to handle Samsung browser autofill
    const formData = new FormData(e.currentTarget);
    const emailValue = (formData.get('email') as string)?.trim() || email;
    const passwordValue = (formData.get('password') as string) || password;

    if (!emailValue || !passwordValue) {
      showError("Missing fields", "Please enter both email and password");
      return;
    }

    setIsLoading(true);

    try {
      await login(emailValue, passwordValue);

      // Check if we should prompt to save biometric credentials
      console.log('[LoginPage] Login successful, checking biometric state:', {
        isBiometricAvailable,
        hasBiometricCredentials,
        biometryTypeName,
      });
      if (isBiometricAvailable && !hasBiometricCredentials) {
        setPendingCredentials({ email, password });
        setShowBiometricPrompt(true);
        showSuccess("Welcome back!", "Successfully signed in");
        setIsLoading(false);
        return;
      }

      showSuccess("Welcome back!", "Successfully signed in");
      navigate(redirectTo);
    } catch (error: any) {
      if (error.data?.code === 'email_not_confirmed' || error.data?.code === 'EMAIL_NOT_CONFIRMED') {
        setEmailNotConfirmed(true);
        try {
          await resendVerification(email);
          showInfo("Verification code sent", "Check your email for the 6-digit code");
        } catch (resendError: any) {
          // Do NOT swallow this. Landing on the OTP screen having been told nothing is
          // indistinguishable, from the user's side, from a code that was sent and lost —
          // which is exactly the report this fix came from. Say what happened; the manual
          // "Resend code" button is right there.
          const retryAfter = resendError?.data?.retryAfterSeconds;
          if (typeof retryAfter === 'number' && retryAfter > 0) {
            setResendCooldown(retryAfter);
          }
          showError("Code not sent", describeApiError(resendError));
        }
      } else if (error.data?.code === 'ACCOUNT_LOCKED') {
        // Redirect to account locked page with lock details
        navigate('/account-locked', {
          state: {
            email,
            lockDetails: error.data?.lockDetails,
          },
        });
      } else {
        showError(
          "Login failed",
          error.message || "Invalid email or password"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Sign up form validation and privacy modal
  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim() || !email || !password || !confirmPassword) {
      showError("Missing fields", "Please fill in all required fields");
      return;
    }

    if (password !== confirmPassword) {
      showError("Passwords don't match", "Please ensure both passwords are identical");
      return;
    }

    // The org-invite route enforces 8 (PASSWORD_TOO_SHORT) where ordinary
    // signup enforces 6. Check the stricter bound up front on that path so the
    // user gets an inline message instead of a round-trip rejection.
    const minPasswordLength = orgInviteToken ? 8 : 6;
    if (password.length < minPasswordLength) {
      showError("Password too short", `Password must be at least ${minPasswordLength} characters`);
      return;
    }

    // CRM used to be validated here — it now lives on the post-login
    // onboarding modal, so the signup form no longer asks for it.

    setShowPrivacyModal(true);
  };

  // Actually perform the signup after privacy acknowledgement
  const handlePrivacyAgreed = async () => {
    setIsLoading(true);
    setEmailError("");

    try {
      // Organization invite: a different endpoint entirely. It validates the
      // invite, creates the account already attached to the org, and
      // auto-confirms the email — so there is no OTP step and we can log the
      // user straight in. Ported verbatim from the retired /signup page.
      if (orgInviteToken) {
        const response = await api.post('/api/workflows/organization/signup-with-team-invite', {
          inviteToken: orgInviteToken,
          email,
          password,
          confirmPassword: password, // the API requires both fields
        });

        if (response.success) {
          showSuccess("Account created!", `Successfully joined ${response.data.organizationName}`);
          setShowPrivacyModal(false);
          await login(email, password);
          navigate('/organization');
        }
        return;
      }

      await signup({
        email,
        password,
        firstName,
        lastName,
        phone: phone || undefined,
        referralCode: referrerId || undefined,
        startTrial: startTrial,
      });

      setShowPrivacyModal(false);
      setSignupComplete(true);
      localStorage.removeItem("pendingTrial");
    } catch (error: any) {
      setShowPrivacyModal(false);

      // Handle duplicate email error from backend
      if (error.message?.toLowerCase().includes('already exists') ||
          error.message?.toLowerCase().includes('already registered') ||
          error.data?.code === 'USER_EXISTS') {
        setEmailError("An account with this email already exists. Please sign in instead.");
        showError(
          "Email already registered",
          "An account with this email already exists. Please sign in instead."
        );
      } else if (error.data?.code === 'EMAIL_SEND_FAILED') {
        // The account exists; only the code did not go out. Treating this as a failed signup
        // stranded the user on a form that would answer "already registered" from then on, with
        // no way to ask for a code. Continue to the OTP screen so "Resend code" is reachable.
        setSignupComplete(true);
        localStorage.removeItem("pendingTrial");
        showError("Code not sent", describeApiError(error));
      } else if (error.data?.code === 'INVITE_EXPIRED') {
        showError("Invitation expired", "This invitation has expired. Please request a new one.");
      } else if (error.data?.code === 'INVITE_ALREADY_USED') {
        showError("Invitation already used", "This invitation has already been used.");
      } else {
        showError(
          "Signup failed",
          error.message || "An error occurred during signup"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // OTP handlers
  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);

    const newOtpCode = [...otpCode];
    newOtpCode[index] = digit;
    setOtpCode(newOtpCode);

    if (digit && index < EMAIL_OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, EMAIL_OTP_LENGTH);

    if (pastedData.length > 0) {
      const newOtpCode = [...otpCode];
      for (let i = 0; i < pastedData.length && i < EMAIL_OTP_LENGTH; i++) {
        newOtpCode[i] = pastedData[i];
      }
      setOtpCode(newOtpCode);

      const nextEmptyIndex = newOtpCode.findIndex(digit => !digit);
      const focusIndex = nextEmptyIndex === -1 ? EMAIL_OTP_LENGTH - 1 : nextEmptyIndex;
      otpInputRefs.current[focusIndex]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const code = otpCode.join('');
    if (code.length !== EMAIL_OTP_LENGTH) {
      showError("Invalid code", `Please enter the complete ${EMAIL_OTP_LENGTH}-digit code`);
      return;
    }

    setIsVerifying(true);
    try {
      await verifyOtp(email, code);
      showSuccess("Email verified!", "Your account is now active");
      // After signup-mode OTP verification, route freshly-verified users to
      // /edit-profile by default. But if a referral invite is pending, the
      // computed redirectTo already points at /referrals — preserve it.
      const isReferralRedirect = referralInviteToken !== null;
      navigate(isReferralRedirect ? redirectTo : (redirectTo === '/dashboard' ? '/edit-profile' : redirectTo));
    } catch (error: any) {
      showError("Verification failed", error.message || "Invalid or expired code");
      setOtpCode(createEmptyOtpDigits());
      otpInputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || isResending) return;

    setIsResending(true);
    try {
      await resendVerification(email);
      showSuccess("Code sent!", "Check your email for a new verification code");
      setResendCooldown(60);
      setOtpCode(createEmptyOtpDigits());
      otpInputRefs.current[0]?.focus();
    } catch (error: any) {
      // The API now distinguishes "we sent it" from "we could not send it", so this branch is
      // reachable — it never was before, because the endpoint answered 200 to every failure.
      // Keep whatever the user has already typed: a resend that failed did not invalidate the
      // code they may still be holding from the first email.
      const retryAfter = error?.data?.retryAfterSeconds;
      if (typeof retryAfter === 'number' && retryAfter > 0) {
        // Sit out the provider's real cooldown rather than our fixed 60s, so the button
        // re-enables exactly when another send would actually be accepted.
        setResendCooldown(retryAfter);
      }
      showError("Code not sent", describeApiError(error));
    } finally {
      setIsResending(false);
    }
  };

  // Reset form when switching modes
  const handleModeSwitch = (newMode: AuthMode) => {
    setMode(newMode);
    setEmailError("");
    setOauthNotice(null);
    // Keep email if switching modes
  };

  // Treatment for the OAuth gate notice. Both remaining codes are
  // informational — the access-key rejection that was the only error case here
  // went away when signup opened to everyone. APPLE_HIDDEN_EMAIL must not
  // inherit the "Sign up to continue" heading: that heading asserts the user has
  // no account, and here we genuinely cannot tell (auth-notices.ts).
  const isOauthNoticeError = false;
  const oauthNoticeTitle =
    oauthNotice?.code === 'APPLE_HIDDEN_EMAIL'
      ? APPLE_HIDDEN_EMAIL_NOTICE.title
      : 'Sign up to continue';

  // Show OTP verification screen (for both sign in unverified and sign up complete)
  if (emailNotConfirmed || signupComplete) {
    return (
      <div className="min-h-screen overflow-y-auto bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white flex flex-col">
        {/* Header */}
        <header className="px-4 py-8 pt-[calc(1.5rem+env(safe-area-inset-top))] md:pt-8">
          <div className="flex items-center justify-center max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
            <img src={tradeLoopLogo} alt="Trade Loop" className="h-10 w-auto" />
          </div>
        </header>

        {/* OTP Verification Form */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="max-w-md md:max-w-2xl lg:max-w-4xl w-full text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Mail className="w-10 h-10 text-white" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">Verify your email</h2>

            <p className="text-gray-600 mb-2">
              We've sent a verification email to:
            </p>

            <p className="text-lg font-semibold text-gray-900 mb-4 break-all">
              {email}
            </p>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-blue-800 font-medium mb-2">Two ways to verify:</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="font-bold">1.</span>
                  <span>Click the <strong>"Verify Email Address"</strong> button in your email to sign in automatically</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">2.</span>
                  <span>Or enter the {EMAIL_OTP_LENGTH}-digit code below:</span>
                </li>
              </ul>
            </div>

            {/* OTP Input */}
            <div className="flex justify-center gap-2 mb-6">
              {otpCode.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (otpInputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  className="w-12 h-14 text-center text-2xl font-bold bg-white/80 backdrop-blur-sm border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl transition-all duration-200"
                  disabled={isVerifying}
                />
              ))}
            </div>

            <Button
              onClick={handleVerifyOtp}
              disabled={isVerifying || otpCode.some(d => !d)}
              className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 mb-4"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify Email"
              )}
            </Button>

            <div className="text-sm text-gray-500 mb-4">
              Didn't receive the code?{" "}
              {isResending ? (
                <span className="text-gray-400">Sending...</span>
              ) : resendCooldown > 0 ? (
                <span className="text-gray-400">Resend code ({resendCooldown}s)</span>
              ) : (
                <Button
                  variant="link"
                  onClick={handleResendCode}
                  className="p-0 h-auto text-blue-600 hover:text-blue-700 font-medium"
                >
                  Resend code
                </Button>
              )}
            </div>

            <Button
              variant="ghost"
              onClick={() => {
                setEmailNotConfirmed(false);
                setSignupComplete(false);
                setOtpCode(createEmptyOtpDigits());
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to {mode === 'signin' ? 'sign in' : 'sign up'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white">
      {/* Header */}
      <header className="px-4 py-8 pt-[calc(1.5rem+env(safe-area-inset-top))] md:pt-8">
        <div className="flex items-center justify-center max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
          <img src={tradeLoopLogo} alt="Trade Loop" className="h-10 w-auto" />
        </div>
      </header>

      {/* Main Content */}
      <div className="px-4 pb-12">
        <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
          {/* Mode Toggle */}
          <div className="mb-8 animate-in slide-in-from-bottom-4 duration-500">
            <div className="flex space-x-1 bg-white shadow-md border border-gray-200 rounded-2xl p-1.5">
              <button
                onClick={() => handleModeSwitch('signin')}
                className={`flex-1 py-3 px-4 text-sm font-semibold rounded-xl transition-all duration-300 ${
                  mode === 'signin'
                    ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => handleModeSwitch('signup')}
                className={`flex-1 py-3 px-4 text-sm font-semibold rounded-xl transition-all duration-300 ${
                  mode === 'signup'
                    ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                    : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                }`}
              >
                Sign Up
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8 animate-in slide-in-from-bottom-4 duration-500">
            <h2 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">
              {mode === 'signin' ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className="text-gray-600 text-lg">
              {mode === 'signin' ? 'Sign in to your Trade Loop account' : 'Join the Trade Loop network'}
            </p>
          </div>

          {/* Secure storage is BROKEN — as distinct from "no saved session".
              This only renders when the native store itself failed (missing
              plugin, OS error), never when the user is simply signed out. It
              exists because the previous version of this code treated those two
              states identically, so a plugin that was never compiled into the
              iOS binary presented as "you were logged out" for months. */}
          {isNative && getStoreFailure() && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-900 mb-1">
                Secure storage unavailable — you will be signed out when the app closes
              </p>
              <p className="text-xs text-amber-800 break-words">{getStoreFailure()}</p>
            </div>
          )}

          {/* Notice for an OAuth attempt the account gate rejected. Rendered
              above BOTH tabs rather than inside the signup one: NO_ACCOUNT
              always arrives with mode already switched to
              signup, but APPLE_HIDDEN_EMAIL deliberately keeps the user on the
              sign-in tab, with the form below it left fully interactive because
              signing in there is the very next thing they should do. */}
          {oauthNotice && (
            <div
              className={`border rounded-xl p-4 mb-6 animate-in slide-in-from-top-2 duration-300 ${
                isOauthNoticeError ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-100'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {isOauthNoticeError ? (
                    <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-semibold mb-1 ${isOauthNoticeError ? 'text-red-900' : 'text-blue-900'}`}>
                    {oauthNoticeTitle}
                  </p>
                  <p className={`text-sm ${isOauthNoticeError ? 'text-red-700' : 'text-blue-700'}`}>
                    {oauthNotice.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sign In: Continue with Google (always available — intent='login', no
              beta gating; the backend rejects an unknown Google identity). The
              signup tab uses its own gated Google block further down. */}
          {mode === 'signin' && (
            <div className="mb-6 animate-in slide-in-from-bottom-4 duration-500 delay-100">
              {/* Hidden on preview deployments — see GOOGLE_SIGN_IN_AVAILABLE. */}
              {GOOGLE_SIGN_IN_AVAILABLE ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleLogin}
                disabled={isLoading || isGoogleLoading}
                className="w-full h-12 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-semibold text-base rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-3"
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
                    />
                  </svg>
                )}
                Continue with Google
              </Button>
              ) : (
                <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                  Google sign-in isn't available on preview deployments. Use email and password.
                </p>
              )}

              {/* Continue with Apple — same intent='login', no beta gating; the
                  backend rejects an unknown Apple identity exactly as it does an
                  unknown Google one. Apple's Human Interface Guidelines call for
                  the black button with the Apple logo at the same prominence as
                  other social buttons, hence the darker treatment here.
                  Hidden on native Android — see APPLE_SIGN_IN_AVAILABLE. */}
              {APPLE_SIGN_IN_AVAILABLE && (
              <Button
                type="button"
                onClick={handleAppleLogin}
                disabled={isLoading || isAppleLoading}
                className="w-full h-12 mt-3 bg-black hover:bg-gray-900 text-white font-semibold text-base rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-3"
              >
                {isAppleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.05 12.54c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.83-.81-3-.79-1.55.02-2.98.9-3.77 2.28-1.61 2.79-.41 6.92 1.15 9.18.76 1.11 1.67 2.35 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.39 0 1.78.74 2.99.72 1.23-.02 2.01-1.13 2.77-2.24.87-1.29 1.23-2.54 1.25-2.6-.03-.01-2.4-.92-2.42-3.65zM14.79 5.4c.63-.77 1.06-1.83.94-2.9-.91.04-2.01.61-2.67 1.37-.59.68-1.1 1.76-.96 2.8 1.01.08 2.05-.51 2.69-1.27z" />
                  </svg>
                )}
                Continue with Apple
              </Button>
              )}

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200/50"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white text-gray-500">
                    or sign in with email
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Sign Up: Trial & Referral Notifications */}
          {mode === 'signup' && (
            <>
              {startTrial && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 mb-6 animate-in slide-in-from-bottom-2 duration-300 shadow-md">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-blue-900 mb-2">Congratulations!</h4>
                      <p className="text-base text-blue-800 leading-relaxed mb-1">
                        Sign up now for your <span className="font-bold">free 30-day trial</span> of our Pro plan!
                      </p>
                      <p className="text-sm text-blue-700">
                        Get unlimited referrals and partner connections, unlimited trades, company branding, and priority support - completely free for 30 days. No credit card required!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {referrerId && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-6 animate-in slide-in-from-bottom-2 duration-300">
                  <div className="flex items-start space-x-3">
                    <UserPlus className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-green-900 mb-1">You've been invited!</h4>
                      <p className="text-sm text-green-700 leading-relaxed">
                        A partner has invited you to join Trade Loop. You'll be automatically connected after signup.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {emailError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 animate-in slide-in-from-top-2 duration-300">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-red-900 mb-1">Email Already Registered</p>
                      <p className="text-sm text-red-700">{emailError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Team invite: the account is created through the invite-specific
                  backend route, which needs an email + password of its own and
                  has no social equivalent — an OAuth identity cannot be matched
                  to the invited address. So the social block below is replaced
                  by this notice rather than shown and left to fail. */}
              {orgInviteToken && (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-6 animate-in slide-in-from-bottom-2 duration-300">
                  <div className="flex items-start gap-3">
                    <UserPlus className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-teal-900 mb-1">You've been invited to a team</h4>
                      <p className="text-sm text-teal-700 leading-relaxed">
                        Create your account below with the email address you were invited at.
                        Team invitations use email and password.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Shared identity block — first + last name are required for
                  EVERY signup path (email/password, Google, Apple). All three
                  Sign Up buttons stay disabled until both name fields are
                  filled. Phone is optional and shared for the same reason:
                  users used to see it duplicated between the OAuth and email
                  paths and think they had to fill it twice. Hidden on the
                  org-invite path since that flow uses a different backend
                  route that does not accept these fields today. */}
              {!orgInviteToken && (
              <div className="bg-white/60 border border-gray-200/60 rounded-2xl p-5 mb-6 animate-in slide-in-from-bottom-4 duration-500 delay-75">
                <h3 className="text-sm font-bold text-gray-900 mb-1">Tell us who you are</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Your name is required to sign up — this unlocks the Google, Apple, and email options below.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-semibold text-gray-700">First Name</Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name"
                      autoComplete="given-name"
                      className="h-12 bg-white border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200"
                      disabled={isLoading || isGoogleLoading || isAppleLoading}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-semibold text-gray-700">Last Name</Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last name"
                      autoComplete="family-name"
                      className="h-12 bg-white border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200"
                      disabled={isLoading || isGoogleLoading || isAppleLoading}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-semibold text-gray-700">Phone Number <span className="text-gray-400 font-normal">(optional)</span></Label>
                  <PhoneInput
                    id="phone"
                    name="phone"
                    value={phone}
                    onChange={setPhone}
                    disabled={isLoading || isGoogleLoading || isAppleLoading}
                    size="lg"
                  />
                </div>
              </div>
              )}

              {/* Social signup — buttons stay disabled until the shared name
                  fields above are filled, at which point either provider will
                  create an account carrying that name (see handleGoogleSignup
                  / handleAppleSignup, which forward it through the OAuth
                  round-trip so the backend backfills the profile). */}
              {!orgInviteToken && (
              <>
              <div className="bg-white/60 border border-gray-200/60 rounded-2xl p-5 mb-6 animate-in slide-in-from-bottom-4 duration-500 delay-100">
                <h3 className="text-sm font-bold text-gray-900 mb-1">
                  {GOOGLE_SIGN_IN_AVAILABLE ? 'Sign up with Google or Apple' : 'Sign up with Apple'}
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Your provider supplies your email and password.
                </p>

                {/* Hidden on preview deployments — see GOOGLE_SIGN_IN_AVAILABLE. */}
                {GOOGLE_SIGN_IN_AVAILABLE && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleSignup}
                  disabled={!canSignup || isGoogleLoading || isLoading}
                  className="w-full h-12 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-semibold text-base rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isGoogleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
                      />
                    </svg>
                  )}
                  Continue with Google
                </Button>
                )}

                {/* Hidden on native Android — see APPLE_SIGN_IN_AVAILABLE. */}
                {APPLE_SIGN_IN_AVAILABLE && (
                <>
                <Button
                  type="button"
                  onClick={handleAppleSignup}
                  aria-describedby="apple-hide-my-email-note"
                  disabled={!canSignup || isAppleLoading || isLoading}
                  className="w-full h-12 mt-3 bg-black hover:bg-gray-900 text-white font-semibold text-base rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isAppleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.05 12.54c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.83-.81-3-.79-1.55.02-2.98.9-3.77 2.28-1.61 2.79-.41 6.92 1.15 9.18.76 1.11 1.67 2.35 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.39 0 1.78.74 2.99.72 1.23-.02 2.01-1.13 2.77-2.24.87-1.29 1.23-2.54 1.25-2.6-.03-.01-2.4-.92-2.42-3.65zM14.79 5.4c.63-.77 1.06-1.83.94-2.9-.91.04-2.01.61-2.67 1.37-.59.68-1.1 1.76-.96 2.8 1.01.08 2.05-.51 2.69-1.27z" />
                    </svg>
                  )}
                  Continue with Apple
                </Button>
                {/* Scenario 5 (APPLE_HIDDEN_EMAIL_LINKING.md §3): signing up with
                    Hide My Email while already having an account silently makes a
                    second one, and no code can detect it — the relay alias carries
                    zero linkage to the real account. Warning first is the entire
                    mitigation. Scoped to Apple only (Google shares this card but
                    has no relay), and it is the Apple button's accessible
                    description rather than a loose paragraph. */}
                <p id="apple-hide-my-email-note" className="text-xs text-gray-500 leading-relaxed mt-2">
                  {APPLE_HIDE_MY_EMAIL_SIGNUP_NOTE}
                </p>
                </>
                )}
                {!canSignup && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                    Enter your first and last name above to enable {GOOGLE_SIGN_IN_AVAILABLE ? 'Google and Apple' : 'Apple'} sign-up.
                  </p>
                )}
              </div>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200/50"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white text-gray-500">
                    or sign up with email
                  </span>
                </div>
              </div>
              </>
              )}
            </>
          )}

          {/* Biometric Login Button */}
          {mode === 'signin' && isBiometricAvailable && hasBiometricCredentials && (
            <div className="mb-6 animate-in slide-in-from-bottom-4 duration-500 delay-100">
              <Button
                type="button"
                onClick={handleBiometricLogin}
                disabled={isLoading || isBiometricAuthenticating}
                className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] flex items-center justify-center gap-3"
              >
                {isBiometricAuthenticating ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    <Fingerprint className="w-6 h-6" />
                    Sign in with {biometryTypeName}
                  </>
                )}
              </Button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200/50"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white text-gray-500">
                    or sign in with email
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Sign In Form */}
          {mode === 'signin' && (
            <form onSubmit={handleSignInSubmit} className="space-y-6 animate-in slide-in-from-bottom-4 duration-500 delay-150">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-gray-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="h-12 bg-white/80 backdrop-blur-sm border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-gray-700">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="h-12 bg-white/80 backdrop-blur-sm border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium pr-12 transition-all duration-200"
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 rounded-lg hover:bg-gray-100/80 transition-colors duration-200"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-500" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>

              <div className="text-center">
                <Link to="/forgot-password">
                  <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200">
                    Forgot your password?
                  </Button>
                </Link>
              </div>
            </form>
          )}

          {/* Sign Up Form */}
          {mode === 'signup' && (
            <form onSubmit={handleSignUpSubmit} className="space-y-6 animate-in slide-in-from-bottom-4 duration-500 delay-150">

              <div className="space-y-2">
                <Label htmlFor="signup-email" className="text-sm font-semibold text-gray-700">
                  Email
                  {orgInviteToken && <span className="ml-2 text-xs text-gray-500">(from your team invite)</span>}
                </Label>
                <Input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError("");
                  }}
                  placeholder="Enter your email"
                  autoComplete="email"
                  className="h-12 bg-white border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  // Locked on the invite path: the backend rejects any address
                  // other than the invited one with EMAIL_MISMATCH.
                  disabled={isLoading || !!orgInviteToken}
                  required
                />
              </div>

              {/* First/last name + phone live in the shared identity block
                  above for the standard signup paths. The org-invite path
                  hides that block (it uses a different backend workflow), so
                  the fields render here in that case so an invited team
                  member can still supply them. */}
              {orgInviteToken && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-sm font-semibold text-gray-700">First Name</Label>
                      <Input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First name"
                        autoComplete="given-name"
                        className="h-12 bg-white border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200"
                        disabled={isLoading}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-sm font-semibold text-gray-700">Last Name</Label>
                      <Input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last name"
                        autoComplete="family-name"
                        className="h-12 bg-white border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200"
                        disabled={isLoading}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-semibold text-gray-700">Phone Number <span className="text-gray-400 font-normal">(optional)</span></Label>
                    <PhoneInput
                      id="phone"
                      name="phone"
                      value={phone}
                      onChange={setPhone}
                      disabled={isLoading}
                      size="lg"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="signup-password" className="text-sm font-semibold text-gray-700">Password</Label>
                <div className="relative">
                  <Input
                    id="signup-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    autoComplete="new-password"
                    className="h-12 bg-white border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium pr-12 transition-all duration-200"
                    disabled={isLoading}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 rounded-lg hover:bg-gray-100/80 transition-colors duration-200"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-500" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-700">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  className="h-12 bg-white border border-gray-200/50 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl text-base font-medium transition-all duration-200"
                  disabled={isLoading}
                  required
                />
              </div>

              {!canSignup && !orgInviteToken && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Enter your first and last name above to enable Create account.
                </p>
              )}

              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isLoading || !canSignup}
              >
                {isLoading ? "Creating account..." : "Create account"}
              </Button>

              <div className="text-center">
                <p className="text-xs text-gray-500">
                  You will be asked to review and agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </form>
          )}

          {/* Get the mobile app.
              Below the form on purpose — it is an alternative way to keep using the account
              being created or signed into, not a competing call to action above it. Renders
              nothing inside the native apps, so this block is inert in the very place a store
              link would be wrong. */}
          {APP_STORE_BADGES_VISIBLE && (
            <div className="mt-8 text-center animate-in slide-in-from-bottom-4 duration-500 delay-300">
              <div className="pt-6 border-t border-gray-200/50">
                <p className="text-sm text-gray-500 mb-3">Trade Loop on your phone</p>
                <AppStoreBadges className="justify-center" />
              </div>
            </div>
          )}

          {/* Back to Home */}
          <div className="mt-6 text-center animate-in slide-in-from-bottom-4 duration-500 delay-300">
            <div className="pt-4 border-t border-gray-200/50">
              <Link to="/">
                <Button variant="ghost" className="text-gray-500 hover:text-gray-700">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Acknowledgement Modal */}
      <PrivacyAcknowledgementModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        onAgree={handlePrivacyAgreed}
        isLoading={isLoading}
      />

      {/* Biometric Save Prompt Modal */}
      {showBiometricPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleSkipBiometric}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
            {/* Icon */}
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Fingerprint className="w-8 h-8 text-white" />
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
              Enable {biometryTypeName}?
            </h3>

            {/* Description */}
            <p className="text-gray-600 text-center mb-6">
              Sign in quickly and securely with {biometryTypeName}. Your credentials will be stored safely on your device.
            </p>

            {/* Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleSaveBiometricCredentials}
                className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
              >
                Enable {biometryTypeName}
              </Button>

              <Button
                variant="ghost"
                onClick={handleSkipBiometric}
                className="w-full h-12 text-gray-500 hover:text-gray-700 font-medium"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
